import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Card, Flex, Row, Col, Skeleton, Switch, Tooltip, Typography, InputRef } from "antd";
import { FilterValue } from "antd/lib/table/interface";
import { WalletOutlined, ShoppingOutlined, CrownOutlined, RiseOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { fetchAnalyticsRows, getSalesPackagesAnalytics } from "hooks/queries/analytics";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { taxChangeSentence } from "pdf/reportText";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import {
  INACTIVE_REASON_LABELS,
  INACTIVE_REASON_TONE,
  UPGRADE_LABEL,
  formatCurrency,
} from "shared/utils";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { getYtdDayRange } from "shared/utils/dayjs";
import { AnalyticsRange, CustomDayRange, PackageType, SalesPackagesAnalytics, SalesPackagesRow } from "shared/types";
import PATHS from "shared/constants/paths";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { DEFAULT_PDF_ROWS, MAX_PDF_ROWS } from "pdf/layout";
import { formatTransactionNumber } from "shared/utils/transactionNumber";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { discountSourceLabel } from "shared/utils/purchaseDiscount";
import { DiscountSource } from "shared/types/enum";
import { InactiveReason } from "shared/types";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { purchasesSpec } from "pdf/reports/specs/archetype2";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// The discount column's filter value for a sale with no discount.
const NO_DISCOUNT = "none";
const DISCOUNT_FILTER_VALUES: string[] = [
  DiscountSource.Code,
  DiscountSource.Loyalty,
  DiscountSource.Referral,
  NO_DISCOUNT,
];
// URL param carrying the Diskon filter, e.g. ?diskon=loyalty,referral.
const DISCOUNT_PARAM = "diskon";
// URL param for the voided switch, e.g. ?dibatalkan=1. On the URL for the
// same reason the Diskon filter is: Back from a purchase's detail page
// restores what the admin was looking at, page memory included.
const SHOW_VOIDED_PARAM = "dibatalkan";

interface PackagePurchasesContentProps {
  type: PackageType;
  title: string;
  backTo: string;
}

// Shared "Pembelian" (purchase log) report — used for both Paket and
// Membership, which are otherwise structurally identical cash-basis
// purchase-history reports scoped to a fixed type (no in-page toggle; the
// type is decided by which route you're on, same pattern as
// PackageOverviewContent). Lives under the Paket/Membership tabs (not
// Pendapatan) since it's a single-entity transaction log, not a
// cross-cutting financial view — see PackageExpiryReportContent for the
// sibling reports it sits alongside.
const PackagePurchasesContent = ({ type, title, backTo }: PackagePurchasesContentProps) => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  // The Diskon filter lives on the URL (written with replace), so Back from a
  // purchase's detail page restores it along with the remembered table page.
  // The server applies it, before its 250-row cap: filtering only the rows
  // already loaded would search just the most recent 250 sales.
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const discountParam = searchParams.get(DISCOUNT_PARAM) ?? "";
  const discountFilter = useMemo(
    () => discountParam.split(",").filter((v) => DISCOUNT_FILTER_VALUES.includes(v)),
    [discountParam],
  );
  const setDiscountFilter = (values: string[]) => {
    const params = new URLSearchParams(searchParams);
    if (values.length) params.set(DISCOUNT_PARAM, values.join(","));
    else params.delete(DISCOUNT_PARAM);
    setSearchParams(params, { replace: true, state: location.state });
  };
  // The name searches stay client-side, but AntD needs every filter column
  // controlled once one is (the Diskon column), or it silently drops the
  // uncontrolled ones. So their values are held here too.
  const [searchFilters, setSearchFilters] = useState<Record<string, FilterValue | null>>({});
  // Voided sales are kept but are not sales: they are outside every card
  // above the table whether or not they are listed, and off the list itself
  // unless the admin asks for them.
  const showVoided = searchParams.get(SHOW_VOIDED_PARAM) === "1";
  const setShowVoided = (next: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set(SHOW_VOIDED_PARAM, "1");
    else params.delete(SHOW_VOIDED_PARAM);
    setSearchParams(params, { replace: true, state: location.state });
    // Back to page one, the way AntD does it for a filter change. The Diskon
    // filter gets this free because AntD routes its reset through the table's
    // onChange; this switch never touches the table, so an admin reading page
    // 6 of a list that is about to shrink would land on an empty page with
    // nothing to explain it.
    handleTableChange({ current: 1 });
  };
  const { data, isLoading, isPlaceholderData } = getSalesPackagesAnalytics(
    range,
    type,
    customRange,
    discountFilter,
    showVoided,
  );
  const voidedCount = (data?.table ?? []).filter((r) => r.is_voided).length;
  // The Pajak column exists only while a listed sale carried tax. AntD keeps
  // an uncontrolled sort in memory after its column leaves and revives it
  // when the column returns, so a sort on Pajak that outlived its column
  // (sorted by Pajak, moved to an untaxed window, moved back) would come
  // back on screen after the page's sort state was cleared, and the PDF
  // would print server order under a Waktu Pembelian caption. So the table
  // is remounted whenever the column set changes (AntD then starts from the
  // default order again) and the page's sort state is cleared with it. The
  // filters and the pager are controlled and survive the remount.
  const taxColumnShown = !!data?.tax?.any;
  useEffect(() => {
    onSortChange(null);
  }, [taxColumnShown, onSortChange]);
  const customerSearchInput = useRef<InputRef>(null);
  const packageSearchInput = useRef<InputRef>(null);
  // One report component, two routes (Paket and Membership) — each keeps its
  // own page-memory slot.
  const { page, handleTableChange } = useRememberedTablePage(`analyticsPurchases.${type}`);
  // Paket and Membership are otherwise identical reports, but a few labels
  // literally say "Paket" — those need to come from the matching namespace
  // instead of always reading from "sales" (same pattern as
  // PackageOverviewContent). The PDF/Excel exports already do this
  // correctly via a local `noun`/`productNoun` variable; this brings the
  // on-screen table in line with them.
  const namespace = type === "membership" ? "membership" : "package";
  // Rows matching the Diskon filter, the period count the pager quotes
  // beside the loaded rows. An older server sends no table_total and never
  // filters.
  const tableTotal = data?.table_total?.value ?? data?.total_rows?.value ?? data?.table.length ?? 0;
  // Four KPI cards fill a row at a quarter each; with the Upgrade card
  // hidden, three would leave a hole, so they take thirds instead.
  const kpiSpan = (data?.upgrades?.value ?? 0) > 0 ? 6 : 8;

  // Columns come from the shared report catalogue (one name, width and
  // alignment per metric); this page keeps its own order, search, filters,
  // chips and sorters.
  const columns: ReportColumn<SalesPackagesRow>[] = [
    reportColumn<SalesPackagesRow>("client", {
      dataIndex: "customer_name",
      // Klien leads this table but the package column holds the long text
      // and the chips, so that one stretches and Klien keeps a fixed share.
      kind: "text",
      share: 16,
      min: 170,
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
      search: getColumnSearchProps<SalesPackagesRow, "customer_name">({
        config: "customer_name",
        searchInput: customerSearchInput,
      }),
      extra: { filteredValue: searchFilters.customer_name ?? null },
    }),
    reportColumn<SalesPackagesRow>(type === "membership" ? "membership" : "package", {
      dataIndex: "package_name",
      // The stretching column of this table (long names plus the chips), not
      // bold: Klien is the row's subject here.
      kind: "name",
      // The name with at most ONE chip beside it (a separate Status column
      // was tried and rejected as one column too many, chris 2026-10-17; two
      // chips beside a long name stacked the cell three lines high).
      // "Voided" is a STATUS (what happened to the sale, with its dot) and
      // wins when both apply (the new half of a cancelled upgrade); its
      // reason, the same word the package card and the Excel print, is one
      // hover away. "Upgrade" is a LABEL (what this row is, no dot) and
      // explains a small amount: an upgrade row shows the fee paid, not the
      // package price. A voided fee counts nowhere, so it needs no such
      // explanation. The dimmed row carries the rest; nothing is struck.
      // The chip always sits UNDER the name (chris, 2026-10-17): one row
      // shape at every window width, and the column sizes to the names
      // alone. The product reports keep their promo chip beside the name,
      // because there most rows carry one and a second line on every row
      // would halve what fits on a screen.
      render: (value: string, row: SalesPackagesRow) => (
        <Flex vertical align="flex-start" gap={4}>
          <span>{value}</span>
          {row.is_voided ? (
            <Tooltip
              placement="topRight"
              title={row.void_reason ? translate(`void_reason.${row.void_reason}`) : undefined}
            >
              <span>
                <StatusBadge
                  tone={INACTIVE_REASON_TONE[InactiveReason.MANUALLY_MARKED_INVALID]}
                  label={INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]}
                />
              </span>
            </Tooltip>
          ) : row.is_upgrade === true ? (
            <StatusBadge tone="navy" label={UPGRADE_LABEL} dot={false} />
          ) : null}
        </Flex>
      ),
      search: getColumnSearchProps<SalesPackagesRow, "package_name">({
        config: "package_name",
        searchInput: packageSearchInput,
      }),
      extra: { filteredValue: searchFilters.package_name ?? null },
    }),
    reportColumn<SalesPackagesRow>("purchased_at", {
      dataIndex: "purchased_at",
      sorter: (a, b) => a.purchased_at.localeCompare(b.purchased_at),
      defaultSortOrder: "descend",
    }),
    reportColumn<SalesPackagesRow>("transaction_id", {
      key: "transaction_number",
      render: (_, row) => (
        <span style={tnum}>{formatTransactionNumber("TRX", row.transaction_number)}</span>
      ),
    }),
    // The four money columns read left to right, on every row and in every
    // tax mode, the faktur pajak's own row: Harga Jual less Diskon plus Pajak
    // equals Total. Harga Jual and Diskon are before tax (the Statement's
    // basis), Pajak is the tax inside or on top of what was paid, Total is
    // the money received.
    // Pajak is listed only when a listed sale carried tax, like the
    // Statement screen's own tax column. Both new columns are sortable only
    // (no filter), so the all-controlled filter rule above is untouched, and
    // their minimums are a little under the money default so the table's
    // scroll threshold grows less.
    reportColumn<SalesPackagesRow>("price_before_tax", {
      dataIndex: "price_before_tax",
      min: 115,
      render: (value: number | null, row: SalesPackagesRow) => (
        <span style={tnum}>
          {value === null || value === undefined ? "-" : formatCurrency(value)}
        </span>
      ),
      sorter: (a, b) => (a.price_before_tax ?? -1) - (b.price_before_tax ?? -1),
    }),
    reportColumn<SalesPackagesRow>("discount", {
      // Explains a lower `price`: without this a discounted sale is
      // indistinguishable from a cheaper one. Every source: the code itself,
      // or Loyalty / Referral, under the amount. The source already implies a
      // non-zero amount (the server never reports a Rp 0 discount); the null
      // check only narrows the type.
      key: "discount",
      kind: "twoLine",
      render: (_, row) => {
        const source = discountSourceLabel(row);
        return source && row.discount_amount !== null && row.discount_amount !== undefined ? (
          <Flex vertical gap={0}>
            <span style={{ ...tnum, fontSize: textPx(13) }}>
              &minus; {formatCurrency(row.discount_amount)}
            </span>
            <Typography.Text type="secondary" style={{ fontSize: textPx(11.5) }}>
              {source}
            </Typography.Text>
          </Flex>
        ) : (
          "-"
        );
      },
      extra: {
        // String values, never booleans (see the AntD filter convention).
        // Controlled and server-side: no onFilter, the rows arrive filtered.
        filters: [
          { text: translate("purchaseDiscount.filter_code"), value: DiscountSource.Code },
          { text: translate("purchaseDiscount.source_loyalty"), value: DiscountSource.Loyalty },
          { text: translate("purchaseDiscount.source_referral"), value: DiscountSource.Referral },
          { text: translate("purchaseDiscount.filter_none"), value: NO_DISCOUNT },
        ],
        filteredValue: discountFilter,
      },
    }),
    ...(data?.tax?.any
      ? [
          reportColumn<SalesPackagesRow>("tax", {
            dataIndex: "tax_amount",
            min: 115,
            render: (value: number | undefined, row: SalesPackagesRow) => (
              <span style={tnum}>
                {value ? formatCurrency(value) : "-"}
              </span>
            ),
            sorter: (a, b) => (a.tax_amount ?? 0) - (b.tax_amount ?? 0),
          }),
        ]
      : []),
    reportColumn<SalesPackagesRow>("total_paid", {
      dataIndex: "price",
      render: (value: number, row: SalesPackagesRow) => (
        <span style={tnum}>
          {formatCurrency(value)}
        </span>
      ),
      sorter: (a, b) => a.price - b.price,
    }),
    // No Kedaluwarsa here (chris, 2026-10-17): a sales row lists the
    // transaction, and a package's expiry is a fact about the package after
    // the sale that can still move (first-use packages have none until the
    // first class, an admin can edit it, an upgrade carries it). It lives in
    // the Kedaluwarsa reports and on the package card; the Excel keeps it as
    // part of its fuller row.
    reportColumn<SalesPackagesRow>("payment_method", {
      dataIndex: "payment_method_name",
      render: (value: string | null) => value ?? "-",
    }),
  ];

  // One source for both exports: the Excel query string and the PDF's
  // larger-row refetch must describe the same window.
  const windowParams = {
    ...(customRange
      ? { from_date: customRange.from, to_date: customRange.to }
      : { range }),
    // So the Excel download and the PDF's larger-row refetch list exactly
    // what the screen lists.
    ...(showVoided ? { show_voided: "true" } : {}),
  };

  // The download fetches the full, unfiltered period when the table holds
  // only the display-capped rows or the Diskon filter is on (the PDF is
  // never filtered); the picker plans on that same set.
  const pdfSpec = useMemo(() => purchasesSpec(type), [type]);
  const pageCount = useReportPageCount(pdfSpec, data, sortState, {
    needsAll:
      !!data &&
      (discountFilter.length > 0 || (data.total_rows?.value ?? data.table.length) > data.table.length),
    loadAll: () =>
      fetchAnalyticsRows<SalesPackagesAnalytics>(
        "sales",
        { ...windowParams, type, view: "packages" },
        MAX_PDF_ROWS,
      ),
  });

  return (
    <>
      <ReportPageHeading
        title={title}
        backTo={backTo}
        extra={
          <ExportMenu
            reportSlug={`pembelian-${type}`}
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  // total_rows, NOT packages_sold: the KPI excludes upgrades
                  // while the table still lists them, so packages_sold would
                  // under-count the rows available to download.
                  // ?? falls back to the rows in hand: an admin deployed
                  // ahead of the server has no total_rows, and the types
                  // cannot warn us because they declare it required.
                  available: Math.min(data.total_rows?.value ?? data.table.length, MAX_PDF_ROWS),
                  total: data.total_rows?.value ?? data.table.length,
                },
                fileName: `spott-${type === "membership" ? "pembelian-membership" : "pembelian-paket"}-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  // The page holds only the display-capped rows, so a bigger
                  // ask needs its own request; fall back to what we have if
                  // that fetch fails rather than losing the download.
                  // The PDF is never filtered: with a Diskon filter on, the
                  // rows in hand are only the matches, so fetch the period.
                  const source =
                    (pageCount.source !== data ? pageCount.source : undefined) ??
                    (discountFilter.length > 0 || (maxRows && maxRows > data.table.length)
                      ? (await fetchAnalyticsRows<SalesPackagesAnalytics>(
                          "sales",
                          { ...windowParams, type, view: "packages" },
                          maxRows ?? DEFAULT_PDF_ROWS,
                        )) ?? data
                      : data);
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: type === "membership" ? "pembelian-membership" : "pembelian-paket",
                    studio: authState.business?.name ?? "",
                    data: source,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {data?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Row gutter={[16, 16]}>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} lg={8}>
                <Card style={{ borderRadius: 16 }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))
          ) : (
            <>
              <Col xs={24} sm={12} lg={kpiSpan}>
                <MetricCard
                  icon={<WalletOutlined />}
                  title={translate("analyticsPage.sales.kpi_total_sales")}
                  information={translate("analyticsPage.columns.sales_info")}
                  value={formatCurrency(data?.total_revenue.value ?? 0)}
                />
              </Col>
              <Col xs={24} sm={12} lg={kpiSpan}>
                <MetricCard
                  icon={<ShoppingOutlined />}
                  title={translate(`analyticsPage.${namespace}.kpi_purchases_count`)}
                  value={data?.packages_sold?.value ?? 0}
                />
              </Col>
              {/* Only when the period actually contains one. Most studios
                  never upgrade, and a permanent "0 Upgrade" card is noise. */}
              {(data?.upgrades?.value ?? 0) > 0 && (
                <Col xs={24} sm={12} lg={kpiSpan}>
                  <MetricCard
                    icon={<RiseOutlined />}
                    title={translate(`analyticsPage.${namespace}.kpi_upgrades`)}
                    value={data?.upgrades?.value ?? 0}
                  />
                </Col>
              )}
              <Col xs={24} sm={12} lg={kpiSpan}>
                <MetricCard
                  icon={<CrownOutlined />}
                  title={
                    data?.best_seller.revenue
                      ? `${translate(`analyticsPage.${namespace}.kpi_purchases_best_seller`)} · ${formatCurrency(data.best_seller.revenue)}`
                      : translate(`analyticsPage.${namespace}.kpi_purchases_best_seller`)
                  }
                  value={data?.best_seller.name ?? "-"}
                  text
                />
              </Col>
            </>
          )}
        </Row>
        <Card styles={{ body: { padding: 18 } }}>
          {/* The void switch lives on the table it filters, not in the period
              row above, which stays identical to every other report's. A
              single switch: voided rows are off the list by default (chris,
              2026-09-29, a sales list shows what was sold) and one flick
              brings them back, greyed and marked. */}
          <Flex justify="space-between" align="center" gap={12} wrap="wrap" style={{ marginBottom: 14 }}>
            {/* Only a note about the FIGURES lives here now; the row count
                moved into the pager. Shown when the listed sales follow more
                than one tax rule, the one case where a reader has to know
                that Pajak is inside Total on some rows and on top of it on
                others. A studio that never switched never sees it. */}
            <div>
              {taxChangeSentence(data?.tax) && (
                <span style={{ fontSize: textPx(13), color: colors.ink3 }}>
                  {taxChangeSentence(data?.tax)}
                </span>
              )}
            </div>
            {/* The count rides the switch that revealed the rows, the way a
                filter chip carries its count; the struck, badged rows say the
                rest. Only known while the switch is on: off, the server
                never sends them. */}
            <Flex align="center" gap={8}>
              <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
                {translate("analyticsPage.sales.voided_show")}
                {showVoided && voidedCount > 0 ? ` (${voidedCount})` : ""}
              </span>
              <Switch size="small" checked={showVoided} onChange={setShowVoided} />
            </Flex>
          </Flex>
          <ReportTable<SalesPackagesRow>
            serverTotal={{ total: tableTotal, noun: translate("pdf.noun.pembelian") }}
            key={taxColumnShown ? "with-tax" : "without-tax"}
            // The customer_package id: a composite like customer + purchased_at
            // is NOT unique (same-batch purchases share a timestamp), and
            // duplicate keys make React duplicate rows on every re-sort. The
            // fallback only covers an older server that doesn't send `id` yet.
            rowKey={(row) => row.id ?? `${row.customer_id}-${row.purchased_at}`}
            loading={isLoading || isPlaceholderData}
            columns={columns}
            dataSource={data?.table ?? []}
            rowClassName={(row) => (row.is_voided ? "row-voided" : "")}
            // This page owns its page memory: the void switch resets the pager.
            pageMemory={{ page, handleTableChange }}
            onSortChange={onSortChange}
            onChange={(_pagination, filters, _sorter, { action }) => {
              if (action === "filter") {
                setSearchFilters({
                  customer_name: filters.customer_name ?? null,
                  package_name: filters.package_name ?? null,
                });
                setDiscountFilter((filters.discount as string[] | null) ?? []);
              }
            }}
            onRow={(row) => ({
              // Goes straight to this purchase's own detail page (not the
              // customer's whole Paket/Membership tab) — that's now the only
              // place to download its Invoice/Receipt.
              onClick: () => navigate(`${PATHS.CUSTOMER_PACKAGE}/${row.id}`),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      </Flex>
    </>
  );
};

export default PackagePurchasesContent;
